import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";

interface Cluster {
  id: string;
  label: string;
  description?: string;
  image_url?: string;
  subCategories: SubCategory[];
}

interface SubCategory {
  id: string;
  label: string;
  description?: string;
}

interface PollResultsProps {
  pollId: string;
  clusters: Cluster[];
  userClusterAllocations: Record<string, number>;
  userSubAllocations: Record<string, number>;
  hasSubmitted: boolean;
}

interface AggregateResult {
  clusterId: string;
  subCategoryId: string | null;
  avgPoints: number;
}

export const PollResults = ({
  pollId,
  clusters,
  userClusterAllocations,
  userSubAllocations,
  hasSubmitted,
}: PollResultsProps) => {
  const [results, setResults] = useState<AggregateResult[]>([]);
  const [totalResponses, setTotalResponses] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadResults();
  }, [pollId]);

  const loadResults = async () => {
    try {
      const { count } = await supabase
        .from("poll_responses")
        .select("*", { count: "exact", head: true })
        .eq("poll_id", pollId);
      setTotalResponses(count || 0);

      const { data: allocations } = await supabase
        .from("poll_allocations")
        .select("cluster_id, sub_category_id, points, response_id")
        .in(
          "response_id",
          (await supabase.from("poll_responses").select("id").eq("poll_id", pollId)).data?.map((r) => r.id) || [],
        );

      if (allocations) {
        const grouped = new Map<string, { total: number; count: number }>();
        allocations.forEach((alloc) => {
          const key = `${alloc.cluster_id}-${alloc.sub_category_id || "null"}`;
          const current = grouped.get(key) || { total: 0, count: 0 };
          grouped.set(key, { total: current.total + alloc.points, count: current.count + 1 });
        });

        const avgResults: AggregateResult[] = [];
        grouped.forEach((value, key) => {
          const [clusterId, subId] = key.split("-");
          avgResults.push({
            clusterId,
            subCategoryId: subId === "null" ? null : subId,
            avgPoints: value.count > 0 ? value.total / value.count : 0,
          });
        });

        setResults(avgResults);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error loading results:", error);
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-8">Loading results...</div>;

  if (totalResponses === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-lg font-medium">No responses yet</p>
        <p className="text-sm text-muted-foreground mt-2">Be the first to share your perspective!</p>
      </div>
    );
  }

  const getClusterAvg = (clusterId: string) =>
    results.find((r) => r.clusterId === clusterId && r.subCategoryId === null)?.avgPoints || 0;
  const getSubAvg = (subId: string) => results.find((r) => r.subCategoryId === subId)?.avgPoints || 0;

  return (
    <TooltipProvider>
      <Card className="p-4 space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-1">Results</h3>
          <p className="text-sm text-muted-foreground">
            Based on {totalResponses} response{totalResponses !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <div className="w-4 h-2 bg-blue-500 rounded-sm"></div>
            <span>Your allocation</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-[2px] h-2 bg-yellow-400"></div>
            <span>Global average</span>
          </div>
        </div>

        <div className="space-y-5">
          {clusters.map((cluster) => {
            const clusterAvg = getClusterAvg(cluster.id);
            const clusterUser = userClusterAllocations[cluster.id] || 0;

            return (
              <div key={cluster.id} className="space-y-2">
                {/* Cluster row */}
                <div className="flex items-start gap-3">
                  {cluster.image_url && (
                    <img
                      src={cluster.image_url}
                      alt={cluster.label}
                      className="w-10 h-10 object-cover rounded-md mt-1"
                    />
                  )}
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <span className="font-medium text-base">{cluster.label}</span>
                        {cluster.description && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-4 h-4 text-muted-foreground cursor-pointer" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs text-sm">{cluster.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground whitespace-nowrap">
                        Avg: {clusterAvg.toFixed(1)} | You: {clusterUser}
                      </div>
                    </div>

                    {/* Cluster bar */}
                    <div className="relative w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mt-1">
                      <div
                        className="absolute top-0 left-0 h-full bg-blue-500 transition-all"
                        style={{ width: `${clusterUser}%` }}
                      />
                      {clusterAvg > 0 && (
                        <div
                          className="absolute top-0 bottom-0 w-[2px] bg-yellow-400 z-10"
                          style={{ left: `calc(${clusterAvg}% - 1px)` }}
                        />
                      )}
                    </div>

                    {/* Subcategories */}
                    <div className="mt-2 space-y-1">
                      {cluster.subCategories.map((sub) => {
                        const subUser = userSubAllocations[sub.id] || 0;
                        const subAvg = getSubAvg(sub.id);
                        const maxWidth = clusterUser; // gray bar limited by cluster allocation

                        return (
                          <div key={sub.id} className="ml-4">
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-1">
                                <span>{sub.label}</span>
                                {sub.description && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Info className="w-3 h-3 text-muted-foreground cursor-pointer" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p className="max-w-xs text-sm">{sub.description}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground whitespace-nowrap">
                                Avg: {subAvg.toFixed(1)} | You: {subUser}
                              </div>
                            </div>

                            {/* Sub bar (scaled to cluster) */}
                            <div
                              className="relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mt-1"
                              style={{ width: `${maxWidth}%` }}
                            >
                              <div
                                className="absolute top-0 left-0 h-full bg-blue-500 transition-all"
                                style={{
                                  width: `${subUser}%`,
                                }}
                              />
                              {subAvg > 0 && (
                                <div
                                  className="absolute top-0 bottom-0 w-[2px] bg-yellow-400 z-10"
                                  style={{ left: `calc(${subAvg}% - 1px)` }}
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </TooltipProvider>
  );
};
